import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, csrfToken, getAccessToken, setAccessToken } from '../api/client'
import type { UserOut } from '../api/models'

type AuthState = {
  user: UserOut | null
  ready: boolean
  signedIn: boolean
  login: (email: string, password: string, totpCode?: string) => Promise<void>
  setup: (email: string, password: string, displayName: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserOut | null>(null)
  const [ready, setReady] = useState(false)
  const queryClient = useQueryClient()

  /**
   * Throws when the profile cannot be loaded. The password may well have been right,
   * but a session nobody can read is not a sign-in - swallowing the error here left the
   * form sitting there with no message and no way forward.
   */
  const loadUser = useCallback(async () => {
    const { data, error } = await api.GET('/api/auth/me', {})
    if (error || !data) {
      setAccessToken(null)
      setUser(null)
      throw error ?? new Error('Die Sitzung konnte nicht geladen werden.')
    }
    setUser(data)
    return data
  }, [])

  // On a cold load the access token is gone but the refresh cookie may still be valid.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      if (!getAccessToken() && csrfToken()) {
        try {
          const response = await fetch('/api/auth/refresh', {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json', 'x-csrf-token': csrfToken() ?? '' },
            body: '{}',
          })
          if (response.ok) {
            const body = (await response.json()) as { access_token: string }
            setAccessToken(body.access_token)
          }
        } catch {
          setAccessToken(null)
        }
      }
      if (getAccessToken()) {
        // A stale token here is ordinary, not an error: fall through to the login form
        // rather than leaving the app on its loading screen for good.
        try {
          await loadUser()
        } catch {
          setAccessToken(null)
          setUser(null)
        }
      }
      if (!cancelled) setReady(true)
    })()
    return () => {
      cancelled = true
    }
  }, [loadUser])

  const login = useCallback(
    async (email: string, password: string, totpCode?: string) => {
      const { data, error } = await api.POST('/api/auth/login', {
        body: { email, password, totp_code: totpCode ?? null, platform: 'web', device_name: 'Browser' },
      })
      if (error || !data) throw error ?? new Error('Anmeldung fehlgeschlagen.')
      setAccessToken(data.access_token)
      await loadUser()
      await queryClient.invalidateQueries()
    },
    [loadUser, queryClient],
  )

  const setup = useCallback(
    async (email: string, password: string, displayName: string) => {
      const { data, error } = await api.POST('/api/setup', {
        body: { email, password, display_name: displayName },
      })
      if (error || !data) throw error ?? new Error('Einrichtung fehlgeschlagen.')
      setAccessToken(data.access_token)
      await loadUser()
      await queryClient.invalidateQueries()
    },
    [loadUser, queryClient],
  )

  const logout = useCallback(async () => {
    await api.POST('/api/auth/logout', { body: {} })
    setAccessToken(null)
    setUser(null)
    queryClient.clear()
  }, [queryClient])

  const value = useMemo<AuthState>(
    () => ({ user, ready, signedIn: Boolean(user), login, setup, logout }),
    [user, ready, login, setup, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden')
  return context
}
